/* eslint-disable import/no-extraneous-dependencies */
import axios from 'axios'
import Qs from 'qs'
import { apiService } from '../_config_/apiservice'
import { catchError } from '../../utils/CatchError/CatchError'
import { mensagemDeErroEmBlob } from '../../utils/ErroEmBlob/ErroEmBlob'

// Central de Resultados (PD-0007). Rotas /central/** : so o header Authorization importa aqui
// (X-TENANT-ID nao e lido por nenhuma delas - sao do banco Master).
//
// ⚠️ Todas exigem super-admin GLOBAL **humano**: desde 2026-09-02 uma X-API-KEY recebe 403 nestas
// rotas, entao nao adianta chamar por integracao.

const ERRO_GENERICO = { message: 'Algo de errado aconteceu!' }

/**
 * Params do ranking, compartilhados entre a TELA e a EXPORTAÇÃO.
 *
 * ⚠️ Um lugar só, de propósito. O backend reusa o mesmo service pelos dois caminhos justamente
 * porque "duas consultas para o mesmo número é como o arquivo e a tela passam a divergir sem
 * ninguém perceber" — duplicar a montagem aqui reintroduziria a divergência pela porta do cliente:
 * bastaria um filtro novo entrar na tela e ninguém lembrar da cópia.
 *
 * ⚠️ Nunca enviar `fases` vazio: a API trata o param AUSENTE como "só EM_FUNCIONAMENTO", que é o
 * default desejado, mas `?fases=` chega como `[null]` no Spring.
 */
const paramsRanking = (
  competencia: string,
  competenciaComparacao: string,
  fases: string[]
) => ({
  ...(competencia ? { competencia } : {}),
  ...(competenciaComparacao ? { competenciaComparacao } : {}),
  ...(fases && fases.length > 0 ? { fases } : {}),
})

/** Multivalor por repeat (?fases=A&fases=B) — mesma convenção do resto do sistema. */
const serializarRepeat = (params: any) =>
  Qs.stringify(params, { arrayFormat: 'repeat' })

export const getCarteiraRanking = async (
  cancel: any,
  competencia: string,
  competenciaComparacao: string,
  fases: string[]
): Promise<CarteiraRankingType | { message: string }> => {
  try {
    const result = await apiService.get('central/resultados/ranking', {
      cancelToken: cancel.token,
      params: paramsRanking(competencia, competenciaComparacao, fases),
      paramsSerializer: serializarRepeat,
    })
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    return catchError(error)
  }
  return ERRO_GENERICO
}

export const getCarteiraTenant = async (
  cancel: any,
  tenantId: string,
  de: string,
  ate: string
): Promise<CarteiraTenantType | { message: string }> => {
  try {
    const result = await apiService.get(
      `central/resultados/tenant/${tenantId}`,
      {
        cancelToken: cancel.token,
        params: {
          ...(de ? { de } : {}),
          ...(ate ? { ate } : {}),
        },
      }
    )
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    return catchError(error)
  }
  return ERRO_GENERICO
}

export const getCarteiraVendedores = async (
  cancel: any,
  tenantId: string,
  competencia: string
): Promise<CarteiraVendedorType[] | { message: string }> => {
  try {
    const result = await apiService.get(
      `central/resultados/tenant/${tenantId}/vendedores`,
      {
        cancelToken: cancel.token,
        // Obrigatorio na API: sem competencia o drill nao tem o que decompor (400).
        params: { competencia },
      }
    )
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    return catchError(error)
  }
  return ERRO_GENERICO
}

export const getCarteiraStatus = async (
  cancel: any
): Promise<CarteiraRecalculoStatusType | { message: string }> => {
  try {
    const result = await apiService.get('central/resultados/status', {
      cancelToken: cancel.token,
    })
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    return catchError(error)
  }
  return ERRO_GENERICO
}

// 202 = disparado (iniciado=true); 409 = ja havia um em andamento (iniciado=false).
// O 409 NAO e erro: e o mesmo corpo, e a tela trata os dois como "esta recalculando" e passa a
// fazer polling. Sem isto o 409 viraria toast vermelho para uma situacao completamente normal.
const tratarRespostaRecalculo = (
  error: unknown
): CarteiraRecalculoStatusType | { message: string } => {
  if (axios.isAxiosError(error) && error.response?.status === 409) {
    return error.response.data as CarteiraRecalculoStatusType
  }
  return catchError(error)
}

// Exportações XLSX (Fase 6). Vêm como BLOB via axios: `window.open` perderia o header
// Authorization e o download voltaria 401.
//
// ⚠️ O erro NÃO passa pelo `catchError` genérico. Acima do teto a API recusa com a orientação de
// como reduzir o recorte, e essa mensagem chega dentro do próprio blob — perdê-la deixaria a pessoa
// com "Algo de errado aconteceu!" e nenhuma ação possível, justamente no momento em que ela precisa
// saber o que fazer.

export const getCarteiraRankingExport = async (
  competencia: string,
  competenciaComparacao: string,
  fases: string[]
): Promise<Blob | { message: string }> => {
  try {
    const result = await apiService.get('central/resultados/export', {
      responseType: 'blob',
      // Os MESMOS params da tela: o arquivo tem de ser o conjunto que está sendo visto.
      params: paramsRanking(competencia, competenciaComparacao, fases),
      paramsSerializer: serializarRepeat,
    })
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    return { message: await mensagemDeErroEmBlob(error) }
  }
  return ERRO_GENERICO
}

export const getCarteiraTenantExport = async (
  tenantId: string,
  de: string,
  ate: string
): Promise<Blob | { message: string }> => {
  try {
    const result = await apiService.get(
      `central/resultados/tenant/${tenantId}/export`,
      {
        responseType: 'blob',
        params: {
          ...(de ? { de } : {}),
          ...(ate ? { ate } : {}),
        },
      }
    )
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    return { message: await mensagemDeErroEmBlob(error) }
  }
  return ERRO_GENERICO
}

export const postCarteiraRecalcular = async (): Promise<
  CarteiraRecalculoStatusType | { message: string }
> => {
  try {
    const result = await apiService.post('central/resultados/recalcular')
    if (result.status === 202 || result.status === 200) {
      return result.data
    }
  } catch (error) {
    return tratarRespostaRecalculo(error)
  }
  return ERRO_GENERICO
}

/**
 * Reabertura de competência (Fase 7) — a única rota do módulo que ESCREVE em dado congelado.
 *
 * ⚠️ Três respostas distintas, e colapsá-las seria o pior erro possível numa ação irreversível:
 * - **200** devolve a competência já recalculada e fechada de novo;
 * - **400** traz na mensagem o MOTIVO da recusa (janela do ERP não cobre mais o mês, competência não
 *   está fechada, é anterior à âncora). Essa frase é o valor da resposta — quem clicou precisa saber
 *   por que não deu, e `catchError` já a extrai do corpo padrão da API;
 * - **409** é ESTADO NEUTRO, não erro: aquela conta está com sync ou rollup em andamento. Vem sem
 *   corpo, então a mensagem é montada aqui — tratá-lo como falha diria "deu erro" para um "tente
 *   daqui a pouco".
 */
export const postReabrirCompetencia = async (
  tenantId: string,
  competencia: string
): Promise<CarteiraCompetenciaType | { message: string }> => {
  try {
    const result = await apiService.post(
      `central/resultados/tenant/${tenantId}/competencia/${competencia}/reabrir`
    )
    if (result.status === 200) {
      return result.data
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return {
        message:
          'Esta conta está com uma operação pesada em andamento (sincronização de vendas ou recálculo). Tente novamente em alguns minutos.',
      }
    }
    return catchError(error)
  }
  return ERRO_GENERICO
}
